import { Component, inject, signal, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BsApiService,
  BsCacheService,
  FeatureFlagService,
  SimpleGlobalModalService,
  type FeatureFlagDisplayRow,
} from '@brightside-web/desktop/data-access/core-services';
import { BrightsideUiModule } from '@brightside/brightside-ui';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Environment } from '@micro-core/environment';
import { ServiceNotificationsService } from '@brightside-web/desktop/feature/service-notifications';
import { LanguageSelectorService } from '@brightside-web/desktop/feature/language-selection';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ssnConfig } from '../ssn-config';
import { Router } from '@angular/router';
import { CreditMockStateService } from '../credit-mock/credit-mock-state.service';
import {
  MOCK_CREDIT_SUMMARY,
  MOCK_CREDIT_SUBSCRIPTION,
} from '../credit-mock/credit-mock.data';
import {
  HAS_CHECKED_ELIGIBILITY_DATA_COLLECT_KEY,
  KnownFeatureFlags,
  ProductEligibilityService,
  RequiredVerificationService,
} from '@brightside-web/desktop/data-access/shared';
import { ProductVerificationGuard } from '@brightside-web/shared/desktop';
import { CardCurrentState } from '@brightside-web/desktop/feature/financial-accounts-core';
import {
  DASHBOARD_RECENT_TRANSACTIONS_DEV_OPTIONS,
  DevMenuService,
  type DashboardRecentTransactionsDevMode,
  type SimulatedCardState,
  type SmsPreferencesDevMode,
} from '../dev-menu.service';
import { SmsPromptService } from '@brightside-web/desktop-v3/feature/preferences';

const DEV_MENU_TAB_SESSION_KEY = 'brightside_dev_menu_active_tab';

export type DevMenuTab = 'dashboard' | 'flags' | 'mocks' | 'prompts';

const DEV_MENU_TABS: { id: DevMenuTab; label: string }[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'flags', label: 'Flags' },
  { id: 'mocks', label: 'Mocks' },
  { id: 'prompts', label: 'Prompts' },
];

const DEV_MENU_FLAG_KEYS: string[] = Object.values(KnownFeatureFlags);

const CARD_STATE_OPTIONS: { value: SimulatedCardState; label: string }[] = [
  { value: null, label: 'Off' },
  { value: 'NONE', label: 'No card' },
  ...(Object.values(CardCurrentState) as CardCurrentState[]).map((v) => ({ value: v, label: v })),
];

@Component({
    selector: 'brightside-web-dev-menu',
    imports: [CommonModule, BrightsideUiModule, TranslateModule],
    templateUrl: './dev-menu.component.html',
    styleUrls: ['./dev-menu.component.scss'],
    animations: [
        trigger('slideToggle', [
            state('hidden', style({
                transform: 'translateY(calc(-100% + 36px))',
                opacity: 0.9,
            })),
            state('visible', style({
                transform: 'translateY(0)',
                opacity: 1,
            })),
            transition('hidden <=> visible', animate('300ms ease')),
        ]),
    ]
})
export class DevMenuComponent {
  @ViewChild('welcomeVideoContent') _welcomeVideoContent: TemplateRef<unknown>;

  showDevMenu = false;
  get allow666(): boolean {
    return ssnConfig.allow666;
  }
  protected _bsApi = inject(BsApiService);
  protected _env = inject(Environment);
  protected _serviceNotificationsService = inject(ServiceNotificationsService);
  protected _devMenuService = inject(DevMenuService);
  protected _router = inject(Router);
  protected _creditMockState = inject(CreditMockStateService);
  protected _bsCache = inject(BsCacheService);
  protected _featureFlagService = inject(FeatureFlagService);
  protected _languageSelector = inject(LanguageSelectorService);
  private _globalModalService = inject(SimpleGlobalModalService);
  private _translateService = inject(TranslateService);
  private _productEligibilityService = inject(ProductEligibilityService);
  private _productVerificationGuard = inject(ProductVerificationGuard);
  private _smsPromptService = inject(SmsPromptService);
  private _requiredVerificationService = inject(RequiredVerificationService);

  readonly flagKeys = DEV_MENU_FLAG_KEYS;
  readonly flagsForDisplay$ = this._featureFlagService.getFlagsForDisplayLive(DEV_MENU_FLAG_KEYS);
  readonly cardStateOptions = CARD_STATE_OPTIONS;
  readonly dashboardTxOptions = DASHBOARD_RECENT_TRANSACTIONS_DEV_OPTIONS;
  readonly devMenuTabs = DEV_MENU_TABS;
  readonly activeTab = signal<DevMenuTab>(DevMenuComponent._readActiveTabFromSession());

  setActiveTab(tab: DevMenuTab): void {
    this.activeTab.set(tab);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(DEV_MENU_TAB_SESSION_KEY, tab);
    }
  }

  private static _readActiveTabFromSession(): DevMenuTab {
    if (typeof sessionStorage === 'undefined') {
      return 'dashboard';
    }
    const raw = sessionStorage.getItem(DEV_MENU_TAB_SESSION_KEY);
    if (raw === 'dashboard' || raw === 'flags' || raw === 'mocks' || raw === 'prompts') {
      return raw;
    }
    return 'dashboard';
  }

  toggleApiService() {
    this._bsApi.toggleService();
  }

  toggleDevMenu() {
    this.showDevMenu = !this.showDevMenu;
  }

  mockNotifications() {
    this._serviceNotificationsService.mockTheNotifications();
  }

  toggleAllow666(): void {
    ssnConfig.setAllow666(!this.allow666);
  }

  toggleCreditScoreInitialCard() {
    this._devMenuService.toggleCreditActionCard();
  }

  navigateToFinancialSnapshot() {
    this._router.navigate(['financialsnapshot-loader']);
  }

  async toggleCreditApiMocks(): Promise<void> {
    const wasOn = this._creditMockState.useCreditMock();
    this._creditMockState.toggleCreditMock();
    if (wasOn) {
      this._bsCache.removeItem('/credit/?filter=summary');
      this._bsCache.removeItem('/credit/subscription');
    } else {
      const oneHourMs = 60 * 60 * 1000;
      await this._bsCache.setItem('/credit/?filter=summary', MOCK_CREDIT_SUMMARY, {
        expires: Date.now() + oneHourMs,
      });
      await this._bsCache.setItem('/credit/subscription', MOCK_CREDIT_SUBSCRIPTION, {
        expires: Date.now() + oneHourMs,
      });
      this._router.navigate(['credit', 'dashboard']);
    }
  }
  
  toggleBadSocureToken(): void {
    this._devMenuService.toggleBadSocureTokenOnNextAttempt();
  }

  toggleProfessionCollectionInjectWhenMissing(): void {
    this._devMenuService.toggleProfessionCollectionInjectWhenMissing();
    this._productEligibilityService.invalidateProductEligibilityCache();
    this._productVerificationGuard.clearStoredEligibilityResponse();
  }

  toggleEligibilityLast4Inject(): void {
    this._devMenuService.toggleEligibilityLast4Inject();
    this._requiredVerificationService.clearRequiredVerifications();
    void this._bsCache.removeItem(HAS_CHECKED_ELIGIBILITY_DATA_COLLECT_KEY);
  }

  isCardStateActive(value: SimulatedCardState): boolean {
    return this._devMenuService.simulatedCardState() === value;
  }

  setCardState(value: SimulatedCardState): void {
    this._devMenuService.setSimulatedCardState(value);
  }

  isDashboardTxActive(value: DashboardRecentTransactionsDevMode | null): boolean {
    return this._devMenuService.dashboardRecentTransactionsOverride() === value;
  }

  setDashboardTxOverride(value: DashboardRecentTransactionsDevMode | null): void {
    this._devMenuService.setDashboardRecentTransactionsOverride(value);
  }

  addMockExternalTransferAccount(): void {
    this._devMenuService.addMockExternalTransferAccount();
  }

  addMockExternalTransferAccountNeedsRelink(): void {
    this._devMenuService.addMockExternalTransferAccountNeedsRelink();
  }

  clearMockExternalTransferAccounts(): void {
    this._devMenuService.clearMockExternalTransferAccounts();
  }

  openTransferFunds(): void {
    void this._router.navigate(['/accounts/transfer-funds']);
  }

  openSmsPrompt(): void {
    this._smsPromptService.open();
  }

  forceSmsPromptOnHome(): void {
    this._devMenuService.setSmsPreferencesDevMode('force-prompt');
  }

  isSmsDevModeActive(mode: SmsPreferencesDevMode): boolean {
    return this._devMenuService.smsPreferencesDevMode() === mode;
  }

  toggleSmsDevMode(mode: Exclude<SmsPreferencesDevMode, 'off' | 'force-prompt'>): void {
    const current = this._devMenuService.smsPreferencesDevMode();
    this._devMenuService.setSmsPreferencesDevMode(current === mode ? 'off' : mode);
  }

  resetSmsDevMode(): void {
    this._devMenuService.setSmsPreferencesDevMode('off');
  }

  openWelcomeVideo(): void {
    if (!this._welcomeVideoContent) return;
    this._globalModalService.createModal({
      title: this._translateService.instant('WELCOME_VIDEO_TITLE'),
      size: 'large',
      displayCloseButton: true,
      eventName: 'dev_menu_welcome_video',
      isError: false,
      templateRef: this._welcomeVideoContent,
      closeAction: () => this._globalModalService.clearModal(),
    });
  }

  setFlagOverride(row: FeatureFlagDisplayRow, value: boolean): void {
    this._featureFlagService.setDevOverride(row.key, value);
  }

  clearFlagOverride(row: FeatureFlagDisplayRow): void {
    this._featureFlagService.setDevOverride(row.key, null);
  }
}
